import React, { useState, useEffect } from "react";
import { Modal, Button, Form, Row, Col, Spinner } from "react-bootstrap";
import PropTypes from "prop-types";
import useApi from "../scripts/apiService";
import { useToken } from "../scripts/tokenContext";
import ReactSelect from "react-select";
import makeAnimated from "react-select/animated";
import { formatDateToBr } from "../scripts/dateFormatter";
import { useTranslation } from "react-i18next";

const ModalExport = ({ show, onHide }) => {
  const { t } = useTranslation();
  const { token } = useToken();
  const [truckId, setTruckId] = useState("");
  const [selectedFields, setSelectedFields] = useState([]);
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [deviceOptions, setDeviceOptions] = useState([]);
  const [fieldsOptions, setFieldsOptions] = useState([]);
  const [exportFormat, setExportFormat] = useState();
  const [error, setError] = useState("");
  const { fetchData, postCSV } = useApi();
  const animatedComponents = makeAnimated();
  const localUser = JSON.parse(localStorage.getItem("user"));
  const [filter, setFilter] = useState();
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (localUser?.user.view_group && token) {
      handleFilterChange(localUser.user.view_group);
    } else {
      handleFilterChange();
    }
  }, [show]);

  const handleFilterChange = async (filter) => {
    if (filter) {
      const response = await fetchData(
        `view/groups/${filter.id}/list-relations`,
      );

      // Assumindo que `response.devices` é o array de dispositivos:
      const deviceLabels = response.data.map((device) => device.device.label);

      setFilter(deviceLabels);
    } else {
      setFilter(null);
    }
  };
  // Fetch devices on component mount
  useEffect(() => {
    if (token) {
      const fetchDevices = async () => {
        try {
          const response = await fetchData("/devices");
          if (filter) {
            const filteredDevices = response.data.filter((device) =>
              filter.includes(device.label),
            );
            setDeviceOptions(filteredDevices);
          } else {
            setDeviceOptions(response.data);
          }
        } catch (error) {
          console.error(error);
        }
      };
      fetchDevices();
    }
  }, [filter]);

  // Reset state when modal is closed
  useEffect(() => {
    if (!show) {
      setTruckId("");
      setSelectedFields([]);
      setStartDate("");
      setEndDate("");
      setFieldsOptions([]);
      setExportFormat();
      setError("");
    }
  }, [show]);

  // Fetch fields for selected device
  const handleDeviceChange = async (device) => {
    setSelectedFields([]);
    setTruckId(device.value);
    try {
      const response = await fetchData(
        `/devices/${device.value}/list-variables`,
      );
      setFieldsOptions(response.data);
    } catch (error) {
      console.error(error);
    }
  };

  // Handle export data
  const handleExportData = async () => {
    try {
      setLoading(true);
      const selectedDevice = deviceOptions.find(
        (device) => device.id === truckId,
      );
      if (!selectedDevice) {
        setError(t("Escolha um ativo"));
        setLoading(false);
        return;
      }
      if (!startDate) {
        setError(t("Selecione a data inicial"));
        setLoading(false);
        return;
      }
      if (!endDate) {
        setError(t("Selecione a data final"));
        setLoading(false);
        return;
      }
      if (!exportFormat) {
        setError(t("Selecione o formato desejado"));
        setLoading(false);
        return;
      }

      let startDateTime = new Date(startDate);
      startDateTime.setSeconds(0);

      let endDateTime = new Date(endDate);
      endDateTime.setSeconds(endDateTime.getSeconds() + 59);

      if (startDateTime > endDateTime) {
        setError(t("A data final precisa ser depois da data incial"));
        setLoading(false);
        return;
      }
      if (startDateTime === endDateTime) {
        setError(t("As datas precisam ser diferentes"));
        setLoading(false);
        return;
      }
      if (endDateTime > new Date()) {
        endDateTime = new Date();
      }

      endDateTime = formatDateToBr(endDateTime);
      startDateTime = formatDateToBr(startDateTime);

      if (selectedFields.length === 0) {
        setError(t("Selecione algum campo para exportar"));
        setLoading(false);
        return;
      }

      const bodyRequest = {
        measurement: selectedDevice.label,
        fields: selectedFields.map((option) => option.value),
        start_datetime: startDateTime,
        end_datetime: endDateTime,
        return_type: exportFormat.value, // Add return type based on selected format
      };

      const response = await postCSV("/measurements/export", bodyRequest);
      const blob = new Blob([response.data], {
        type:
          exportFormat.value === "csv"
            ? "text/csv"
            : "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });

      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute(
        "download",
        `Exported_Data_${selectedDevice.label}.${exportFormat.value}`,
      );
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      window.URL.revokeObjectURL(url);
      setLoading(false);
      setError("");
      onHide(true);
    } catch (error) {
      setError(t("Não foi possível encontrar dados nesse intervalo de tempo"));
      setLoading(false);
      console.error(error);
    }
  };

  // Options for export format select
  const formatOptions = [
    { label: "CSV", value: "csv" },
    { label: "XLSX", value: "xlsx" },
  ];

  // Options for device and fields select
  const deviceOptionsSelect = deviceOptions.map((data) => ({
    label: data.name,
    value: data.id,
  }));

  const fieldsOptionsSelect = fieldsOptions.map((data) => ({
    label: data.name,
    value: data.treated_name,
  }));

  return (
    <Modal
      backdrop="static"
      centered
      size="lg"
      show={show}
      onHide={() => onHide(false)}
    >
      <Modal.Header closeVariant="white" closeButton>
        <Modal.Title>{t("Exportar dados")}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Form>
          <ReactSelect
            placeholder={t("De qual ativo deseja exportar os dados *")}
            className="mb-4"
            options={deviceOptionsSelect}
            closeMenuOnSelect={true}
            components={animatedComponents}
            onChange={handleDeviceChange}
            value={deviceOptionsSelect.find(
              (option) => option.value === truckId,
            )}
          />
          <Row>
            <Col>
              <Form.Group className="mb-4" controlId="formStartDate">
                <Form.Label>{t("Data inicial")}</Form.Label>
                <Form.Control
                  type="datetime-local"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                  required
                />
              </Form.Group>
            </Col>
            <Col>
              <Form.Group className="mb-4" controlId="formEndDate">
                <Form.Label>{t("Data final")}</Form.Label>
                <Form.Control
                  type="datetime-local"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                  required
                />
              </Form.Group>
            </Col>
          </Row>
          <ReactSelect
            placeholder={t("Variáveis para exportar *")}
            className="mb-4"
            options={fieldsOptionsSelect}
            closeMenuOnSelect={false}
            isMulti
            components={animatedComponents}
            onChange={setSelectedFields}
            value={selectedFields}
          />
          <ReactSelect
            placeholder={t("Formato de exportação *")}
            className="mb-4"
            options={formatOptions}
            components={animatedComponents}
            onChange={setExportFormat}
            value={exportFormat}
          />
        </Form>
        {error && (
          <div className="text-danger">
            <br></br>
            {error}
          </div>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="primary" onClick={loading ? null : handleExportData}>
          {loading ? <Spinner animation="border" size="sm" /> : t("Exportar")}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

ModalExport.propTypes = {
  show: PropTypes.bool.isRequired,
  onHide: PropTypes.func.isRequired,
};

export default ModalExport;
